import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from '../reducers/history';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { Shelf } from 'lib/modelBuilder/entities/Shelf';
import { makeSelectShelvesLevelSelected } from 'containers/Configurator/selectors/selection';
import { HistoryStep } from 'containers/Configurator/types';
import { makeSelectCabinet, makeSelectSelectedIdCabinet } from './furniture';
import { Furniture } from '../../../lib/modelBuilder/entities/Furniture';

/**
 * Direct selector to the app state domain
 */

const selectHistoryDomain = (state: ApplicationRootState) => {
  if (state.configurator) {
    return state.configurator.history || initialState;
  }
  return initialState;
};

export const makeSelectHistoryIndice = () =>
  createSelector(selectHistoryDomain, (substate): number => substate.indice);

export const makeSelectSteps = () =>
  createSelector(selectHistoryDomain, (substate): HistoryStep[] => {
    return substate.steps;
  });

export const makeSelectLastHistoryCabinet = () =>
  createSelector(
    selectHistoryDomain,
    makeSelectSelectedIdCabinet(),
    (substate, selectedCabinetId): Cabinet => {
      // Get the last cabinet of selected cabinet

      if (substate.steps && substate.steps[substate.indice]) {
        const activeHistoryStep = substate.steps[substate.indice];
        if (
          activeHistoryStep.selectedId !== null &&
          selectedCabinetId !== null
        ) {
          const cabinet = activeHistoryStep.furniture.get(selectedCabinetId);
          if (cabinet) return cabinet;
          throw new Error('The current selected id is not found in history');
        } else {
          if (activeHistoryStep.furniture.first)
            return activeHistoryStep.furniture.first;
        }
      }
      throw new Error('There is no cabinet in history');
    },
  );

export const makeSelectLastHistoryFurniture = () =>
  createSelector(
    selectHistoryDomain,
    (substate): Furniture => {
      if (substate.steps && substate.steps[substate.indice]) {
        const activeHistoryStep = substate.steps[substate.indice];
        if (activeHistoryStep.selectedId !== null) {
          const furniture = activeHistoryStep.furniture;
          if (furniture) return furniture;
          throw new Error('The current selected id is not found in history');
        } else {
          if (activeHistoryStep.furniture) return activeHistoryStep.furniture;
        }
      }
      throw new Error('There is no furniture in history');
    },
  );

export const makeSelectIsUndoActive = () =>
  createSelector(
    selectHistoryDomain,
    (substate): boolean =>
      substate.indice < substate.steps.length && substate.indice !== 0,
  );

export const makeSelectIsRedoActive = () =>
  createSelector(selectHistoryDomain, (substate): boolean => {
    const totalSteps = substate.steps.length;
    return (
      totalSteps > 0 &&
      substate.indice < totalSteps &&
      substate.indice !== totalSteps - 1
    );
  });

export const makeSelectLastHistoryCornersColor = () =>
  createSelector(makeSelectLastHistoryCabinet(), cabinet => {
    if (cabinet) return cabinet.cornersColor();
    return undefined;
  });

export const makeSelectShelvesSelectedLastHistory = () =>
  createSelector(
    makeSelectLastHistoryCabinet(),
    makeSelectShelvesLevelSelected(),
    (cabinet, levelArray): Shelf[] => {
      if (cabinet) {
        // All is selected if there is no level in level array
        if (levelArray.length === 0) return cabinet.shelves;
        // Filter the selected level from shelves
        return cabinet.shelves.filter(item => levelArray.includes(item.level));
      }
      return [];
    },
  );

export const makeSelectIsLastShelvesSelected = () =>
  /**
   * Check if the selection have the last shelf in it
   */
  createSelector(
    makeSelectLastHistoryCabinet(),
    makeSelectShelvesLevelSelected(),
    (cabinet, levelArray): boolean => {
      if (cabinet) {
        if (levelArray.length === 0) return true;
        return !!levelArray.find(level => level === cabinet.shelves.length - 1);
      }
      return false;
    },
  );

export default selectHistoryDomain;
